import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useHistoryStore } from './stores/useHistoryStore';

export const useHistory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const initialMount = useRef(true);
  const navigationLock = useRef(false);

  const { history, push, pop, replace, lastPushedPathname, setLastPushedPathname } =
    useHistoryStore(
      useShallow((state) => ({
        history: state.history,
        push: state.push,
        pop: state.pop,
        replace: state.replace,
        lastPushedPathname: state.lastPushedPathname,
        setLastPushedPathname: state.setLastPushedPathname,
      })),
    );

  const safePush = useCallback(
    (url: string) => {
      if (!navigationLock.current) {
        navigationLock.current = true;
        push(url);
        navigationLock.current = false;
      }
    },
    [push],
  );

  const navigateBack = useCallback(
    (defaultPrevUrl: string) => {
      const previousPathname = history[history.length - 2];
      pop();
      if (previousPathname) {
        navigate(previousPathname, { replace: true });
        return;
      }
      navigate(defaultPrevUrl);
    },
    [history, pop, navigate],
  );

  useEffect(() => {
    const currentPathname = `${location.pathname}`;

    if (initialMount.current) {
      initialMount.current = false;
      if (history.length === 0) {
        setLastPushedPathname(currentPathname);
        safePush(currentPathname);
      }
      return;
    }

    if (currentPathname === lastPushedPathname) {
      return;
    }
    setLastPushedPathname(currentPathname);
    if (history.length === 0) {
      safePush(currentPathname);
      return;
    }

    switch (navigationType) {
      case 'PUSH':
        if (history[history.length - 1] !== currentPathname) {
          safePush(currentPathname);
        }
        break;
      case 'REPLACE':
        if (history[history.length - 1] !== currentPathname) {
          replace(currentPathname);
        }
        break;
      case 'POP':
        pop();
        break;
      default:
    }
  }, [location, navigationType]);

  return { navigateBack };
};
